/**
 * MODIFIED FROM SHADCN ORIGINAL
 *
 * This component has been customized from the original shadcn/ui version.
 * Modifications: Added SkeletonText and SkeletonEmailPreview utilities
 *
 * Original: https://ui.shadcn.com/
 */

import { cn } from '@ophelos/ui/lib/utils';

function Skeleton({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="skeleton" className={cn('bg-muted animate-pulse rounded-md', className)} {...props} />;
}

/**
 * Skeleton Text
 * Pre-configured skeleton for multiple text lines with natural width variation
 */
interface SkeletonTextProps {
  lines?: number;
  className?: string;
}

function SkeletonText({ lines = 3, className }: SkeletonTextProps) {
  const lineItems = Array.from({ length: lines }, (_, i) => ({ id: `line-${i}`, isLast: i === lines - 1 }));

  return (
    <div className={cn('space-y-2', className)}>
      {lineItems.map(({ id, isLast }) => (
        <Skeleton key={id} className={cn('h-4', isLast && lines > 1 ? 'w-4/5' : 'w-full')} />
      ))}
    </div>
  );
}

/**
 * Skeleton Email Preview
 * Pre-configured skeleton matching email template structure
 */
function SkeletonEmailPreview() {
  return (
    <div className="space-y-6">
      {/* Subject Line */}
      <div className="space-y-2">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="h-10 w-full" />
      </div>

      {/* Email Body */}
      <div className="space-y-4 rounded-lg border bg-white p-6">
        {/* Header */}
        <div className="space-y-2 rounded-md bg-muted/30 p-4">
          <Skeleton className="h-6 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </div>

        {/* Content */}
        <div className="space-y-3">
          <SkeletonText lines={4} />
        </div>

        {/* Access Code Skeleton */}
        <div className="rounded-md border-2 border-muted p-6">
          <Skeleton className="mx-auto mb-4 h-6 w-40" />
          <div className="mb-4 flex justify-center gap-2">
            {['digit-1', 'digit-2', 'digit-3', 'digit-4', 'digit-5', 'digit-6'].map((digitId) => (
              <Skeleton key={digitId} className="h-14 w-12" />
            ))}
          </div>
          <Skeleton className="mx-auto h-4 w-48" />
        </div>

        {/* Button */}
        <div className="text-center">
          <Skeleton className="mx-auto h-10 w-32" />
        </div>
      </div>
    </div>
  );
}

export { Skeleton, SkeletonEmailPreview, SkeletonText };
